import React, { useState, useRef } from 'react';
import { View } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useUserNotifications } from '../context/UserNotificationsContext';
import { NotificationDropdown } from './NotificationDropdown';
import {
    Settings, Globe, MessageCircle, PawPrint, Heart, User, Map, LayoutDashboard,
    ShoppingCart, GraduationCap, Users, Bell, LayoutGrid, Siren, X
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';

interface SidebarProps {
    currentView: View;
    onChangeView: (view: View) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ currentView, onChangeView }) => {
    const { language, setLanguage, t } = useLanguage();
    const { user } = useAuth();
    const { unreadCount } = useUserNotifications();
    const [showNotifDropdown, setShowNotifDropdown] = useState(false);

    // Id del botón bajo el dedo mientras se desliza por el menú inferior (efecto dock),
    // sin necesidad de levantar el dedo entre opciones. Al soltar el dedo, navega a
    // donde haya quedado (si hubo arrastre real entre iconos distintos).
    const [touchHoverId, setTouchHoverId] = useState<string | null>(null);
    const touchStartIdRef = useRef<string | null>(null);
    const draggedRef = useRef(false);
    const suppressClickRef = useRef(false);

    const idFromPoint = (x: number, y: number): string | null => {
        const el = document.elementFromPoint(x, y) as HTMLElement | null;
        const btn = el?.closest('[data-nav-id]') as HTMLElement | null;
        return btn?.getAttribute('data-nav-id') || null;
    };

    const handleNavTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        if (!touch) return;
        const id = idFromPoint(touch.clientX, touch.clientY);
        touchStartIdRef.current = id;
        draggedRef.current = false;
        setTouchHoverId(id);
    };

    const handleNavTouch = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        if (!touch) return;
        const id = idFromPoint(touch.clientX, touch.clientY);
        setTouchHoverId(id);
        if (id && id !== touchStartIdRef.current) draggedRef.current = true;
    };

    const handleNavTouchEnd = () => {
        if (draggedRef.current && touchHoverId) {
            suppressClickRef.current = true;
            onChangeView(touchHoverId as View);
        }
        setTouchHoverId(null);
        touchStartIdRef.current = null;
    };

    const clearNavTouch = () => {
        setTouchHoverId(null);
        touchStartIdRef.current = null;
        draggedRef.current = false;
    };

    const target = import.meta.env.VITE_APP_TARGET || 'USER';

    let menuItems = [];

    if (!user) {
        // Para usuarios no logueados (todos los entornos en su pantalla pre-login si la hay)
        menuItems = [
            { id: View.LANDING, label: 'Inicio', icon: LayoutDashboard },
            { id: View.LOST_PETS_HALL, label: 'SOS', icon: Siren },
            { id: View.GPS_TRACKING, label: 'Mapa', icon: Map }
        ];
    } else {
        // Menús personalizados según APK target
        if (target === 'VET') {
            menuItems = [
                { id: View.VET_DASHBOARD, label: 'Panel Vet', icon: LayoutDashboard },
                { id: View.PET_PROFILES, label: 'Pacientes', icon: PawPrint },
                { id: View.PET_ADVICE, label: 'Consultas', icon: MessageCircle }
            ];
        } else if (target === 'SHELTER') {
            menuItems = [
                { id: View.SHELTER_DASHBOARD, label: 'Albergue', icon: LayoutDashboard },
                { id: View.GPS_TRACKING, label: 'Rescates', icon: Map },
                { id: View.LOST_PETS_HALL, label: 'SOS', icon: Siren }
            ];
        } else if (target === 'BREEDER') {
            menuItems = [
                { id: View.BREEDER_DASHBOARD, label: 'Criadero', icon: LayoutDashboard },
                { id: View.PET_PROFILES, label: 'Camadas', icon: PawPrint },
                { id: View.MARKETPLACE, label: 'Tienda', icon: ShoppingCart }
            ];
        } else {
            // Default USER target
            // SOS ya no vive en el menú principal — se movió a una tarjeta roja
            // en la vista principal (además del botón flotante que ya existía en el mapa).
            menuItems = [
                { id: View.GPS_TRACKING, label: 'Mapa', icon: Map },
                { id: View.PET_FEED, label: 'Comunidad', icon: MessageCircle },
                { id: View.PET_PROFILES, label: t?.nav?.pets || 'Mascotas', icon: PawPrint },
                { id: View.PET_ADVICE, label: t?.nav?.health || 'Salud IA', icon: Heart },
            ];
        }

        // Si es root, agregamos TODAS las pestañas disponibles para debug, 
        // o podemos mantener el comportamiento original
        // Solo en la web: en cada APK el menú es el de su propio entorno, sin atajos a otros.
        if (!Capacitor.isNativePlatform() && (user?.role?.includes('root') || user?.email === 'gioh@amigopeludo.cl')) {
            menuItems = [
                { id: View.USER_HUB, label: 'Inicio', icon: LayoutDashboard },
                { id: View.COMMAND_CENTER, label: 'Comando', icon: LayoutGrid },
                { id: View.PET_FEED, label: 'Comunidad', icon: MessageCircle },
                { id: View.ACADEMY, label: 'Academia', icon: GraduationCap },
                { id: View.PLAYDATES, label: 'Playdates', icon: Users },
                { id: View.GPS_TRACKING, label: 'Mapa', icon: Map },
                { id: View.PET_ADVICE, label: 'Salud IA', icon: Heart },
                { id: View.COMMUNITY_ALERTS, label: 'Alertas', icon: Bell },
                { id: View.LOST_PETS_HALL, label: 'SOS', icon: Siren },
                { id: 'ROOT_PANEL' as View, label: 'Admin', icon: Settings }
            ];
        }
    }


    const toggleLanguage = () => {
        setLanguage(language === 'es' ? 'en' : 'es');
    };

    return (
        <>
            {/* --- DESKTOP (Top Bar) --- */}
            <nav className="fixed top-0 left-0 w-full z-[100] bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-sm hidden lg:block transition-all">
                <div className="max-w-full mx-auto px-8 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2 cursor-pointer group" onClick={() => onChangeView(user ? View.USER_HUB : View.LANDING)}>
                        <img src="/logo.png" alt="Amigo Peludo" className="h-10 w-auto object-contain" />
                    </div>

                    <div className="flex items-center gap-6">
                        {menuItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = currentView === item.id;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => onChangeView(item.id)}
                                    className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl transition-all duration-300 ${isActive
                                        ? 'bg-[#FF6B00] text-white shadow-xl shadow-orange-600/30 scale-105'
                                        : 'text-slate-500 hover:bg-slate-50'
                                        }`}
                                >
                                    <Icon size={18} />
                                    <span className="font-black text-xs uppercase italic tracking-tight">{item.label}</span>
                                </button>
                            );
                        })}
                        <div className="ml-4 pl-4 border-l border-slate-100 flex items-center gap-4">
                            <button onClick={toggleLanguage} className="p-2 rounded-xl text-slate-400 hover:text-[#FF6B00] transition-colors ring-1 ring-slate-100 ring-inset">
                                <Globe size={18} />
                            </button>

                            {/* Notification Bell */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowNotifDropdown(!showNotifDropdown)}
                                    className={`p-2 rounded-xl text-slate-400 hover:text-[#FF6B00] transition-all ring-1 ring-slate-100 ring-inset relative ${showNotifDropdown ? 'bg-slate-50 text-[#FF6B00]' : ''}`}
                                >
                                    <Bell size={18} />
                                    {unreadCount > 0 && (
                                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white text-[8px] font-black flex items-center justify-center rounded-full border-2 border-white">
                                            {unreadCount}
                                        </span>
                                    )}
                                </button>
                                {showNotifDropdown && (
                                    <NotificationDropdown
                                        onClose={() => setShowNotifDropdown(false)}
                                        onViewAll={() => {
                                            onChangeView(View.NOTIFICATIONS);
                                            setShowNotifDropdown(false);
                                        }}
                                    />
                                )}
                            </div>

                            <button
                                onClick={() => onChangeView(View.USER_PROFILE)}
                                className={`group relative w-10 h-10 rounded-xl overflow-hidden border-2 transition-all hover:scale-105 active:scale-95 ${currentView === View.USER_PROFILE ? 'border-[#FF6B00] shadow-lg shadow-orange-500/20' : 'border-slate-100 hover:border-[#FF6B00]/30'}`}
                            >
                                <img
                                    src={user?.imageUrl || `https://ui-avatars.com/api/?name=${user?.name || 'U'}&background=FF6B00&color=fff`}
                                    alt="Profile"
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* --- MOBILE (RINGO DYNAMIC MENU) --- */}
            {/* Encapsulado: siempre "fit-content" (ancho de los iconos), incluso en
                nativo — antes se estiraba a todo el ancho de la pantalla. */}
            <nav className={`fixed left-0 w-full z-[100] lg:hidden flex justify-center pointer-events-none ${Capacitor.isNativePlatform() ? 'bottom-0 px-4 pb-[env(safe-area-inset-bottom)]' : 'bottom-0 px-4'}`}>
                <div className="bg-white/90 backdrop-blur-3xl border border-slate-200 shadow-[0_20px_50px_rgba(0,0,0,0.1)] flex items-center h-16 px-3 pointer-events-auto transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] rounded-[2.5rem]"
                    style={{ width: 'fit-content', minWidth: '280px' }}>

                    <div
                        className="flex items-center justify-center w-full gap-1"
                        onTouchStart={handleNavTouchStart}
                        onTouchMove={handleNavTouch}
                        onTouchEnd={handleNavTouchEnd}
                        onTouchCancel={clearNavTouch}
                    >
                        {menuItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = currentView === item.id;
                            const isHover = touchHoverId === String(item.id);
                            const lifted = isActive || isHover;

                            return (
                                <button
                                    key={item.id}
                                    data-nav-id={String(item.id)}
                                    onClick={() => {
                                        if (suppressClickRef.current) { suppressClickRef.current = false; return; }
                                        onChangeView(item.id);
                                    }}
                                    onMouseEnter={() => setTouchHoverId(String(item.id))}
                                    onMouseLeave={() => setTouchHoverId(prev => prev === String(item.id) ? null : prev)}
                                    className={`relative flex flex-col items-center justify-center transition-all duration-200 outline-none
                                        ${isActive ? 'px-5 py-1.5' : 'px-3 py-1.5'}`}
                                >
                                    {/* Icon: se agranda al pasar el dedo (isHover) y se mantiene elevado si está activo */}
                                    <div className={`transition-all duration-200 flex items-center justify-center
                                        ${lifted ? 'text-[#FF6B00]' : 'text-slate-500 opacity-60'}
                                        ${isHover ? '-translate-y-3 scale-125' : isActive ? '-translate-y-2 scale-110' : ''}`}>
                                        <Icon size={lifted ? 23 : 20} strokeWidth={lifted ? 3 : 2} />
                                        {lifted && (
                                            <div className="absolute -inset-2 bg-[#FF6B00]/20 blur-xl rounded-full -z-10 animate-pulse"></div>
                                        )}
                                    </div>

                                    {/* Label appearing below when activo o bajo el dedo */}
                                    <div className={`absolute bottom-[-2px] transition-all duration-200 overflow-hidden
                                        ${lifted ? 'max-h-5 opacity-100 scale-100' : 'max-h-0 opacity-0 scale-50'}`}>
                                        <span className="text-[9px] font-black uppercase text-[#FF6B00] tracking-tighter italic whitespace-nowrap">
                                            {item.label}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </nav>

            {/* --- MOBILE TOP BAR: oculto en APK nativa --- */}
            {!Capacitor.isNativePlatform() && <div className="fixed top-0 left-0 w-full z-[100] lg:hidden h-[calc(4rem+env(safe-area-inset-top))] bg-white/50 backdrop-blur-xl border-b border-white/5 flex items-end justify-between px-6 pb-3 pt-[env(safe-area-inset-top)] pointer-events-none">
                <div className="flex items-center gap-2 pointer-events-auto" onClick={() => onChangeView(user ? View.USER_HUB : View.LANDING)}>
                    <img src="/logo.png" alt="Amigo Peludo" className="h-8 w-auto object-contain" />
                </div>

                <div className="pointer-events-auto flex items-center gap-3">
                    {/* Mobile Bell */}
                    <div className="relative">
                        <button
                            onClick={() => setShowNotifDropdown(!showNotifDropdown)}
                            className={`w-9 h-9 rounded-2xl flex items-center justify-center border-2 transition-all relative ${showNotifDropdown ? 'bg-[#FF6B00]/10 border-[#FF6B00] text-[#FF6B00]' : 'bg-white border-white/50 text-slate-400 shadow-inner'}`}
                        >
                            <Bell size={18} />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white text-[8px] font-black flex items-center justify-center rounded-full border-2 border-white">
                                    {unreadCount}
                                </span>
                            )}
                        </button>
                        {showNotifDropdown && (
                            <NotificationDropdown
                                onClose={() => setShowNotifDropdown(false)}
                                onViewAll={() => {
                                    onChangeView(View.NOTIFICATIONS);
                                    setShowNotifDropdown(false);
                                }}
                            />
                        )}
                    </div>


                    <button
                        onClick={() => onChangeView(View.USER_PROFILE)}
                        className={`w-9 h-9 rounded-2xl overflow-hidden border-2 transition-all ${currentView === View.USER_PROFILE ? 'border-[#FF6B00] shadow-md transform scale-105' : 'border-white/50 shadow-inner'}`}
                    >
                        <img
                            src={user?.imageUrl || `https://ui-avatars.com/api/?name=${user?.name || 'U'}&background=FF9800&color=fff`}
                            alt="P"
                            className="w-full h-full object-cover"
                        />
                    </button>
                </div>
            </div>}
        </>
    );
};